import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import {
  Search,
  ArrowRight,
  Star,
  Laptop,
  HardHat,
  Clipboard,
  Users,
  Calculator,
  ShoppingCart,
  Headphones,
  Calendar,
  Heart,
  Building,
  Truck,
  FileText,
  Briefcase,
  PenTool,
} from "lucide-react";
import HomeBanner from "./components/HomeBanner";

const HomePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");

  const handleSearch = (e) => {
    e.preventDefault();
    navigate(`/jobs?search=${encodeURIComponent(searchTerm)}`);
  };

  const testimonials = [
    {
      id: 1,
      name: "Jack B.",
      title: t('home.testimonials.controller'),
      quote: t('home.testimonials.quote1'),
      rating: 5,
    },
    {
      id: 2,
      name: "Stuart S.",
      title: t('home.testimonials.frequencyManager'),
      quote: t('home.testimonials.quote2'),
      rating: 5,
    },
    {
      id: 3,
      name: "Maria R.",
      title: "Administrative Assistant",
      quote: t('home.testimonials.quote3'),
      rating: 5,
    }
  ];

  const industries = [
    {
      icon: <Laptop className="w-10 h-10 text-green-500" />,
      title: t('home.industries.technology'),
      description: t('home.industries.technologyDesc'),
    },
    {
      icon: <HardHat className="w-10 h-10 text-green-500" />,
      title: t('home.industries.construction'),
      description: t('home.industries.constructionDesc'),
    },
    {
      icon: <Calculator className="w-10 h-10 text-green-500" />,
      title: t('home.industries.finance'),
      description: t('home.industries.financeDesc'),
    },
    {
      icon: <ShoppingCart className="w-10 h-10 text-green-500" />,
      title: t('home.industries.retail'),
      description: t('home.industries.retailDesc'),
    },
    {
      icon: <Headphones className="w-10 h-10 text-green-500" />,
      title: t('home.industries.customerService'),
      description: t('home.industries.customerServiceDesc'),
    },
    {
      icon: <Calendar className="w-10 h-10 text-green-500" />,
      title: t('home.industries.administrative'),
      description: t('home.industries.administrativeDesc'),
    },
  ];

  return (
    <div className="bg-background min-h-screen flex flex-col">
      {/* Banner Section */}
      <HomeBanner />
      
      {/* Hero Section */}
      <section className="relative text-white py-20">
        <div className="absolute inset-0 z-0">
          <img
            src="/images/emp_hero.webp"
            alt="Two diverse professionals high-fiving in a modern office setting"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/90 to-primary-dark/80 backdrop-blur-sm"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              {t('home.hero.mainTitle')}
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-white/90">
              {t('home.hero.subtitle')}
            </p>

            {/* Search Form */}
            <form onSubmit={handleSearch} className="max-w-3xl mx-auto mb-8">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-accent/70" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={t('home.hero.searchPlaceholder')}
                    className="w-full pl-10 pr-4 py-3 rounded-lg text-gray-900 border border-white/20 focus:border-accent focus:ring-2 focus:ring-accent/50 bg-white/95 backdrop-blur-sm"
                  />
                </div>
                <button
                  type="submit"
                  className="bg-accent text-white px-8 py-3 rounded-lg font-semibold hover:bg-accent-dark transition-duration-300 shadow-lg hover:shadow-xl"
                >
                  {t('home.hero.searchButton')}
                </button>
              </div>
            </form>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
              <Link
                to="/general-application"
                className="bg-white/90 text-primary px-8 py-4 rounded-lg font-semibold hover:bg-white transition-colors flex items-center justify-center shadow-lg hover:shadow-xl backdrop-blur-sm group"
              >
                <FileText className="mr-2 group-hover:scale-110 transition-transform" />
                {t('home.hero.uploadResume')}
              </Link>
              <Link
                to="/jobs"
                className="bg-primary-dark/80 text-white px-8 py-4 rounded-lg font-semibold hover:bg-primary-dark transition-colors flex items-center justify-center shadow-lg hover:shadow-xl backdrop-blur-sm group"
              >
                <Briefcase className="mr-2 group-hover:scale-110 transition-transform" />
                {t('home.hero.browseJobs')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Welcome Message Section */}
      <section className="py-20 bg-gradient-to-b from-white to-accent/5">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="prose prose-lg mx-auto">
              <p className="text-lg text-accent-800/80 mb-6">
                {t('home.welcome.paragraph1')}
              </p>
              <p className="text-lg text-accent-800/80 mb-6">
                {t('home.welcome.paragraph2')}
              </p>
              <p className="text-lg text-accent-800/80">
                {t('home.welcome.paragraph3')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gradient-to-b from-accent/5 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
              {t('home.services.title')}
            </h2>
            <p className="text-xl text-accent-700">
              {t('home.services.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-duration-300 border-b-4 border-primary group">
              <Users className="w-12 h-12 text-accent mb-4 group-hover:text-primary transition-colors group-hover:scale-110 transform duration-300" />
              <h3 className="text-xl font-semibold mb-2 text-gray-900">
                {t('home.services.temporary.title')}
              </h3>
              <p className="text-accent-700">
                {t('home.services.temporary.description')}
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-duration-300 border-b-4 border-primary group">
              <Clipboard className="w-12 h-12 text-accent mb-4 group-hover:text-primary transition-colors group-hover:scale-110 transform duration-300" />
              <h3 className="text-xl font-semibold mb-2 text-gray-900">
                {t('home.services.tempToHire.title')}
              </h3>
              <p className="text-accent-700">
                {t('home.services.tempToHire.description')}
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-duration-300 border-b-4 border-primary group">
              <FileText className="w-12 h-12 text-accent mb-4 group-hover:text-primary transition-colors group-hover:scale-110 transform duration-300" />
              <h3 className="text-xl font-semibold mb-2 text-gray-900">
                {t('home.services.directHire.title')}
              </h3>
              <p className="text-accent-700">
                {t('home.services.directHire.description')}
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-duration-300 border-b-4 border-primary group">
              <Users className="w-12 h-12 text-accent mb-4 group-hover:text-primary transition-colors group-hover:scale-110 transform duration-300" />
              <h3 className="text-xl font-semibold mb-2 text-gray-900">
                {t('home.services.staffAugmentation.title')}
              </h3>
              <p className="text-accent-700">
                {t('home.services.staffAugmentation.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="py-20 bg-gradient-to-b from-white to-accent/5">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
              {t('home.industries.title')}
            </h2>
            <p className="text-xl text-accent-700">
              {t('home.industries.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {industries.map((industry, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-duration-300 border-b-4 border-primary group">
                <div className="text-accent mb-4 group-hover:text-primary transition-colors group-hover:scale-110 transform duration-300">
                  {industry.icon}
                </div>
                <h3 className="text-xl font-semibold mb-2 text-gray-900">{industry.title}</h3>
                <p className="text-accent-700">{industry.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              {t('home.testimonials.title')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('home.testimonials.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <div
                key={testimonial.id}
                className="bg-white p-6 rounded-lg shadow-lg"
              >
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star
                      key={i}
                      className="w-5 h-5 text-yellow-400 fill-current"
                    />
                  ))}
                </div>
                <p className="text-gray-600 mb-4">{testimonial.quote}</p>
                <div>
                  <p className="font-semibold">{testimonial.name}</p>
                  <p className="text-gray-600">{testimonial.title}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-primary text-white py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="text-center md:text-left">
              <h3 className="text-2xl font-bold mb-4">
                {t('home.cta.candidates')}
              </h3>
              <p className="mb-6">{t('home.cta.candidatesDesc')}</p>
              <Link
                to="/general-application"
                className="inline-flex items-center bg-accent text-white px-6 py-3 rounded-lg font-semibold hover:bg-accent-dark transition-duration-300"
              >
                {t('home.cta.candidatesButton')}
                <ArrowRight className="ml-2" />
              </Link>
            </div>
            <div className="text-center md:text-left">
              <h3 className="text-2xl font-bold mb-4">
                {t('home.cta.employers')}
              </h3>
              <p className="mb-6">{t('home.cta.employersDesc')}</p>
              <Link
                to="/employers"
                className="inline-flex items-center bg-accent text-white px-6 py-3 rounded-lg font-semibold hover:bg-accent-dark transition-duration-300"
              >
                {t('home.cta.employersButton')}
                <ArrowRight className="ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
